import { useState } from "react";
import { editStaff } from "../services/staffHandler"; // 确保引入的路径是正确的
import { uploadData } from 'aws-amplify/storage';

interface Staff {
  id: string;
  name: string;
  role: string;
  description: string;
  certificates: string;
  experience: string;
  imageUrl: string;
}

interface EditEmployeePageProps {
  staff: Staff;
  onClose: () => void;
}

const EditEmployeePage: React.FC<EditEmployeePageProps> = ({ staff, onClose }) => {
  const [name, setName] = useState<string>(staff.name);
  const [role, setRole] = useState<string>(staff.role);
  const [description, setDescription] = useState<string>(staff.description);
  const [certificates, setCertificates] = useState<string>(staff.certificates);
  const [experience, setExperience] = useState<string>(staff.experience);
  const [photo, setPhoto] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [formError, setFormError] = useState<string>("");

  const handlePhotoUpload = async (file: File): Promise<string> => {
    const fileName = `public/staff/${Date.now()}_${file.name}`;
    try {
      const result = await uploadData({
        path: fileName,
        data: file,
        options: {
          onProgress: ({ transferredBytes, totalBytes }) => {
            if (totalBytes) {
              setUploadProgress(Math.round((transferredBytes / totalBytes) * 100));
            }
          },
        },
      }).result;

      return result.path;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error('Photo upload failed.');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    if (!name || !role || !description || !certificates || !experience) {
      setFormError("Please fill in all fields.");
      return;
    }

    try {
      let photoPath = staff.imageUrl; 

      if (photo) {
        photoPath = await handlePhotoUpload(photo);
      }

      const updatedStaff: Staff = {
        id: staff.id,
        name,
        role,
        description,
        certificates,
        experience,
        imageUrl: photoPath,
      };

      await editStaff(updatedStaff.id, updatedStaff);

      alert("Employee updated successfully!");
      onClose();
    } catch (error: any) {
      setFormError(error.message || "An error occurred while updating the employee.");
    }
  };

  return (
    <div className="main">
      <div className="border p-5 rounded-3xl shadow-lg mx-auto mt-20 bg-white max-w-2xl">
        <h1 className="text-2xl mb-4 font-bold text-center">Edit Employee</h1>
        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
          <div>
            <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">
              Employee Name
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
              required
            />
          </div>
          <div>
            <label htmlFor="role" className="block mb-2 text-sm font-medium text-gray-900">
              Role
            </label>
            <input
              type="text"
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
              required
            />
          </div>
          <div>
            <label htmlFor="description" className="block mb-2 text-sm font-medium text-gray-900">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
              required
            />
          </div>
          <div>
            <label htmlFor="certificates" className="block mb-2 text-sm font-medium text-gray-900">
              Certificates
            </label>
            <textarea
              id="certificates"
              value={certificates}
              onChange={(e) => setCertificates(e.target.value)}
              rows={2}
              className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
              required
            />
          </div>
          <div>
            <label htmlFor="experience" className="block mb-2 text-sm font-medium text-gray-900">
              Experience
            </label>
            <textarea
              id="experience"
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              rows={2}
              className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
              required
            />
          </div>
          <div>
            <label htmlFor="photo" className="block mb-2 text-sm font-medium text-gray-900">
              Photo (Leave blank to keep current photo)
            </label>
            <input
              type="file"
              id="photo"
              onChange={(e) => setPhoto(e.target.files ? e.target.files[0] : null)}
              className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
            />
          </div>
          {uploadProgress > 0 && (
            <p className="text-blue-500 text-sm">Upload progress: {uploadProgress}%</p>
          )}
          {formError && <p className="text-red-500 text-sm">{formError}</p>}
          <button
            type="submit"
            className="w-full text-white bg-blue-600 hover:bg-blue-700 rounded-lg text-sm px-5 py-2.5 text-center font-bold"
          >
            Save Changes
          </button>
        </form>
        <div className="mt-4 text-center">
          <button type="button" onClick={onClose} className="cancel-button">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEmployeePage;
